// /pages/HR/components/charts/ComboChart.js
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import './ComboChart.css';

// Chart.js is registered globally via chartConfig.js

/**
 * Combination Chart Component (Bar + Line)
 * Displays bars with a trend line overlay
 */
const ComboChart = React.memo(({
  barData = [],
  lineData = [],
  labels = [],
  barLabel = 'Count',
  lineLabel = 'Average',
  xAxisLabel = '',
  yAxisLabel = '',
  y2AxisLabel = '',
  showLegend = true,
  showGrid = true,
  height = 350,
  loading = false,
  error = null,
  barColors = null,
  lineColor = '#4B5563',
  lineStyle = 'smooth',
  showDataPoints = true,
  animate = true,
  className = '',
  onDataPointClick = null,
  showTrendLine = false,
  showAnnotations = false,
}) => {
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState(null);
  const [annotations, setAnnotations] = useState([]);

  // Calculate statistics for annotations - memoized
  const calculateStats = useCallback((data) => {
    if (!data || data.length === 0) return null;

    const numbers = data.filter(d => typeof d === 'number');
    const max = Math.max(...numbers);
    const min = Math.min(...numbers);
    const avg = numbers.reduce((a, b) => a + b, 0) / numbers.length;

    return { max, min, avg };
  }, []);

  // Process data
  useEffect(() => {
    if (!barData || barData.length === 0) {
      return;
    }

    // Calculate trend line if needed
    let trendLineData = null;
    if (showTrendLine && barData.length > 1) {
      const n = barData.length;
      const sumX = barData.reduce((sum, _, i) => sum + i, 0);
      const sumY = barData.reduce((sum, val) => sum + val, 0);
      const sumXY = barData.reduce((sum, val, i) => sum + (i * val), 0);
      const sumX2 = barData.reduce((sum, _, i) => sum + (i * i), 0);

      const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
      const intercept = (sumY - slope * sumX) / n;

      trendLineData = barData.map((_, i) => slope * i + intercept);
    }

    // Calculate annotations
    if (showAnnotations) {
      const barStats = calculateStats(barData);

      const newAnnotations = [];
      if (barStats) {
        newAnnotations.push({
          type: 'line',
          yMin: barStats.avg,
          yMax: barStats.avg,
          borderColor: 'rgba(255, 99, 132, 0.5)',
          borderDash: [5, 5],
          label: {
            content: `Avg: ${barStats.avg.toFixed(1)}`,
            enabled: true,
            position: 'end'
          }
        });
      }
      setAnnotations(newAnnotations);
    }

    // Handle bar colors - if array provided, use as-is; otherwise use default
    let backgroundColors = barColors;

    // If barColors is not provided or is null, use default yellow color
    if (!barColors) {
      backgroundColors = '#FFC000';
    }

    // If it's a single color string, apply to all bars
    if (typeof backgroundColors === 'string') {
      backgroundColors = new Array(barData.length).fill(backgroundColors);
    }

    // If it's an array but shorter than data length, extend it
    if (Array.isArray(backgroundColors) && backgroundColors.length < barData.length) {
      const defaultColor = '#FFC000';
      while (backgroundColors.length < barData.length) {
        backgroundColors.push(defaultColor);
      }
    }

    // Prepare chart data
    const datasets = [
      {
        type: 'bar',
        label: barLabel,
        data: barData,
        backgroundColor: backgroundColors,
        borderColor: Array.isArray(backgroundColors) ?
          backgroundColors.map(c => c) : backgroundColors,
        borderWidth: 1,
        borderRadius: 4,
        order: 2,
        yAxisID: 'y',
      }
    ];

    // Add line dataset if provided
    if (lineData && lineData.length > 0) {
      datasets.push({
        type: 'line',
        label: lineLabel,
        data: lineData,
        borderColor: lineColor,
        backgroundColor: lineColor + '20',
        borderWidth: 2,
        pointRadius: showDataPoints ? 4 : 0,
        pointHoverRadius: 6,
        pointBackgroundColor: '#fff',
        pointBorderColor: lineColor,
        pointBorderWidth: 2,
        tension: lineStyle === 'smooth' ? 0.4 : 0,
        stepped: lineStyle === 'stepped',
        fill: false,
        order: 1,
        yAxisID: y2AxisLabel ? 'y2' : 'y',
      });
    }

    // Add trend line if calculated
    if (trendLineData) {
      datasets.push({
        type: 'line',
        label: 'Trend',
        data: trendLineData,
        borderColor: 'rgba(128, 128, 128, 0.5)',
        borderDash: [10, 5],
        borderWidth: 2,
        pointRadius: 0,
        fill: false,
        order: 3,
        yAxisID: 'y',
      });
    }

    setChartData({
      labels: labels,
      datasets: datasets
    });
  }, [
    barData, lineData, labels, barColors, lineColor, lineStyle,
    showDataPoints, showTrendLine, showAnnotations, barLabel,
    lineLabel, y2AxisLabel, calculateStats
  ]);

  // Memoized click handler
  const handleChartClick = useCallback((event, elements) => {
    if (elements.length > 0 && onDataPointClick && chartData) {
      const element = elements[0];
      const datasetIndex = element.datasetIndex;
      const index = element.index;
      const dataset = chartData.datasets[datasetIndex];

      onDataPointClick({
        type: dataset.type,
        label: chartData.labels[index],
        value: dataset.data[index],
        datasetLabel: dataset.label,
        index,
        datasetIndex
      });
    }
  }, [onDataPointClick, chartData]);

  // Memoized retry handler
  const handleRetry = useCallback(() => {
    // TODO: API INTEGRATION POINT
    console.log('Retrying chart data fetch...');
  }, []);

  // Chart configuration - memoized
  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    animation: animate ? {
      duration: 1000,
      easing: 'easeOutQuart'
    } : false,
    plugins: {
      legend: {
        display: showLegend,
        position: 'top',
        labels: {
          font: {
            family: 'Imprima, sans-serif',
            size: 12
          },
          padding: 15,
          usePointStyle: true,
          pointStyle: 'rectRounded'
        }
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        padding: 12,
        cornerRadius: 8,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            const value = context.parsed.y;
            if (context.dataset.type === 'line' && y2AxisLabel) {
              label += value.toFixed(2);
            } else {
              label += new Intl.NumberFormat('en-US').format(value);
            }
            return label;
          },
          afterBody: function (tooltipItems) {
            if (tooltipItems.length > 0) {
              const index = tooltipItems[0].dataIndex;
              if (index > 0 && barData.length > index) {
                const currentBar = barData[index];
                const previousBar = barData[index - 1];
                const change = ((currentBar - previousBar) / previousBar * 100).toFixed(1);
                return `Change: ${change > 0 ? '+' : ''}${change}%`;
              }
            }
            return null;
          }
        }
      },
      annotation: showAnnotations ? {
        annotations: annotations
      } : {}
    },
    scales: {
      x: {
        grid: {
          display: showGrid,
          drawBorder: false,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            family: 'Imprima, sans-serif',
            size: 11
          },
          color: '#666',
          maxRotation: 45,
          minRotation: 0
        },
        title: {
          display: !!xAxisLabel,
          text: xAxisLabel,
          font: {
            family: 'Imprima, sans-serif',
            size: 12,
            weight: 'bold'
          },
          color: '#333'
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        grid: {
          display: showGrid,
          drawBorder: false,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            family: 'Imprima, sans-serif',
            size: 11
          },
          color: '#666',
          callback: function (value) {
            return new Intl.NumberFormat('en-US', {
              notation: 'compact',
              compactDisplay: 'short'
            }).format(value);
          }
        },
        title: {
          display: !!yAxisLabel,
          text: yAxisLabel,
          font: {
            family: 'Imprima, sans-serif',
            size: 12,
            weight: 'bold'
          },
          color: '#333'
        }
      },
      y2: y2AxisLabel ? {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          font: {
            family: 'Imprima, sans-serif',
            size: 11
          },
          color: '#666'
        },
        title: {
          display: true,
          text: y2AxisLabel,
          font: {
            family: 'Imprima, sans-serif',
            size: 12,
            weight: 'bold'
          },
          color: '#333'
        }
      } : undefined
    },
    onClick: handleChartClick
  }), [
    animate, showLegend, showGrid, showAnnotations,
    xAxisLabel, yAxisLabel, y2AxisLabel, annotations,
    handleChartClick, barData
  ]);

  // Loading state
  if (loading) {
    return (
      <div className={`combo-chart-container loading ${className}`} style={{ height }}>
        <div className="chart-skeleton">
          <div className="skeleton-bars">
            {[...Array(7)].map((_, i) => (
              <div
                key={i}
                className="skeleton-bar"
                style={{
                  height: `${Math.random() * 60 + 20}%`,
                  animationDelay: `${i * 0.1}s`
                }}
              />
            ))}
          </div>
          <div className="skeleton-line"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`combo-chart-container error ${className}`} style={{ height }}>
        <div className="chart-error">
          <span className="error-icon">📈</span>
          <p className="error-message">{error}</p>
          <button
            className="retry-button"
            onClick={handleRetry}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!chartData || chartData.datasets[0].data.length === 0) {
    return (
      <div className={`combo-chart-container no-data ${className}`} style={{ height }}>
        <div className="no-data-message">
          <span className="no-data-icon">📈</span>
          <p>No data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`combo-chart-container ${className}`} style={{ height }}>
      <Chart
        ref={chartRef}
        type='bar'
        data={chartData}
        options={options}
      />
    </div>
  );
});

ComboChart.displayName = 'ComboChart';

export default ComboChart;